import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { authHeaders } from "@/lib/authToken";
import { cloneMcpGrants, type McpGrants } from "@shared/mcpGrants";

export function LibrarianPreviewCard({ grants }: { grants?: McpGrants }) {
  const [query, setQuery] = useState("What did we decide about pricing?");
  const mutation = useMutation({
    mutationFn: async (q: string) => {
      const headers = await authHeaders();
      const res = await fetch("/api/v1/librarian/ask", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, mcpGrants: grants ?? cloneMcpGrants(), limit: 6 }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? "Preview failed");
      return body as { hits?: Array<{ title: string; source: string; snippet: string }>; hint?: string; count?: number };
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pack preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          See the snippet pack Claude would receive. This uses the same Librarian as MCP.
        </p>
        <Textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={2} />
        <Button
          size="sm"
          onClick={() => mutation.mutate(query.trim())}
          disabled={!query.trim() || mutation.isPending}
        >
          {mutation.isPending ? "Searching…" : "Preview pack"}
        </Button>
        {mutation.isError && <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>}
        {mutation.data && (
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              {mutation.data.count ?? 0} hits. {mutation.data.hint}
            </p>
            {(mutation.data.hits ?? []).map((hit, i) => (
              <div key={`${hit.title}-${i}`} className="rounded-md border p-2">
                <p className="font-medium">
                  {hit.title} <span className="text-xs text-muted-foreground">({hit.source})</span>
                </p>
                <p className="text-muted-foreground">{hit.snippet}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
